/**
 * AdminMasterInspoPage - Iter 9.40+ Slice C²
 *
 * SIMPLIFIED to match the canonical model:
 *   • 6 inspo categories: Hair · Nails · Face · Food · Lashes · Style
 *   • Each has ONE "Completed Experiences" folder (no sub-folders)
 *   • Folder media = host- or client-uploaded photos of the FINAL look
 *   • Each folder carries:
 *       – visibility (public · pilots-only · hidden)
 *       – linked-experience tags (multi-select from db.master_experiences)
 *
 * The same experience-tag system feeds every preference card, so admins can
 * filter "all hair preferences linked to Bridal experience" etc.
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Loader2, Save, FolderTree, Globe2, Lock, Users2, Plus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const INSPO_CATEGORIES = [
  { id: "hair",   label: "Hair",   tone: "#FECACA" },
  { id: "nails",  label: "Nails",  tone: "#FBCFE8" },
  { id: "face",   label: "Face",   tone: "#FED7AA" },
  { id: "food",   label: "Food",   tone: "#FEF08A" },
  { id: "lashes", label: "Lashes", tone: "#DDD6FE" },
  { id: "style",  label: "Style",  tone: "#BFDBFE" },
];

const VISIBILITY_OPTIONS = [
  { value: "public",  label: "Public",     icon: Globe2,  hint: "Visible to everyone, including non-logged-in users." },
  { value: "pilots",  label: "Pilots only",icon: Users2,  hint: "Only visible to pilot hosts and admins." },
  { value: "hidden",  label: "Hidden",     icon: Lock,    hint: "Hidden from everyone except admins." },
];

const DEFAULT_FOLDER_NAME = "Completed Experiences";

export default function AdminMasterInspoPage({ embedded = false }) {
  const [folders, setFolders] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const seedFolders = () => INSPO_CATEGORIES.map(c => ({
    folder_id: `${c.id}_completed`,
    name: DEFAULT_FOLDER_NAME,
    category: c.id,
    visibility: "public",
    linked_experiences: [],
    media_count: 0,
  }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load saved folders
      const r = await fetch(`${API}/admin/master-inspo/folders`, { headers: auth() });
      let saved = [];
      if (r.ok) { const d = await r.json(); saved = d.folders || []; }

      // Make sure every category has a Completed Experiences folder
      const haveSet = new Set(saved.map(f => f.category));
      const merged = [
        ...saved,
        ...INSPO_CATEGORIES.filter(c => !haveSet.has(c.id)).map(c => ({
          folder_id: `${c.id}_completed`,
          name: DEFAULT_FOLDER_NAME,
          category: c.id,
          visibility: "public",
          linked_experiences: [],
          media_count: 0,
        })),
      ];
      setFolders(merged);

      // Load experiences for tag picker
      const r2 = await fetch(`${API}/admin/master-experiences`, { headers: auth() });
      if (r2.ok) {
        const d2 = await r2.json();
        setExperiences(d2.experiences || d2 || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (catId, patch) => {
    setFolders(fs => fs.map(f => f.category === catId ? { ...f, ...patch } : f));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/master-inspo/folders`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ folders }),
      });
      if (r.ok) toast.success(`${folders.length} folders saved - synced to /me Inspo and host dashboards`);
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 max-w-5xl mx-auto"} data-testid="admin-master-inspo-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-stone-700" />
          <div>
            <h1 className="text-xl font-bold">Master Inspo</h1>
            <p className="text-xs text-stone-500">One <strong>Completed Experiences</strong> folder per category. Hosts and clients upload photos of the final look. Tag each folder to the experiences it can be planned with.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="inspo-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="inspo-grid">
        {INSPO_CATEGORIES.map(cat => {
          const folder = folders.find(f => f.category === cat.id);
          if (!folder) return null;
          return (
            <FolderCard
              key={cat.id}
              cat={cat}
              folder={folder}
              experiences={experiences}
              onUpdate={(patch) => update(cat.id, patch)}
            />
          );
        })}
      </div>
    </div>
  );
}

function FolderCard({ cat, folder, experiences, onUpdate }) {
  const expsForCat = experiences.filter(e => !e.category || e.category === cat.id);
  const linkedSet = new Set(folder.linked_experiences || []);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(folder.name || DEFAULT_FOLDER_NAME);
  const toggleExp = (eid) => {
    const next = linkedSet.has(eid)
      ? (folder.linked_experiences || []).filter(x => x !== eid)
      : [...(folder.linked_experiences || []), eid];
    onUpdate({ linked_experiences: next });
  };
  const saveName = () => {
    const n = (nameDraft || "").trim() || DEFAULT_FOLDER_NAME;
    onUpdate({ name: n });
    setNameDraft(n);
    setEditingName(false);
  };

  return (
    <Card data-testid={`inspo-card-${cat.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 flex items-center justify-center font-bold text-stone-900" style={{ backgroundColor: cat.tone, borderRadius: 8 }}>
            {cat.label[0]}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold capitalize">{cat.label}</h3>
            {editingName ? (
              <div className="flex items-center gap-1.5 mt-1">
                <input
                  autoFocus
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setNameDraft(folder.name); setEditingName(false); } }}
                  className="flex-1 text-[11px] border border-stone-200 rounded px-2 py-0.5"
                  data-testid={`inspo-folder-name-input-${cat.id}`}
                />
              </div>
            ) : (
              <button onClick={() => setEditingName(true)} className="text-[11px] text-stone-500 hover:text-stone-900 underline decoration-dotted" data-testid={`inspo-folder-name-${cat.id}`}>
                {folder.name || DEFAULT_FOLDER_NAME}
              </button>
            )}
            <p className="text-[10px] text-stone-400">{folder.media_count || 0} media</p>
          </div>
        </div>

        {/* Visibility radio */}
        <div className="mb-3" data-testid={`inspo-visibility-${cat.id}`}>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-1.5">Visibility</Label>
          <div className="grid grid-cols-3 gap-1.5">
            {VISIBILITY_OPTIONS.map(v => {
              const Icon = v.icon;
              const active = folder.visibility === v.value;
              return (
                <button
                  key={v.value}
                  onClick={() => onUpdate({ visibility: v.value })}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] transition ${active ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-600 hover:bg-stone-900 hover:text-white hover:border-stone-900"}`}
                  data-testid={`inspo-visibility-${cat.id}-${v.value}`}
                  title={v.hint}
                >
                  <Icon className="w-3 h-3" />{v.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Linked experience tags */}
        <div data-testid={`inspo-experiences-${cat.id}`}>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-1.5">
            Linked experiences <span className="text-stone-400 normal-case">· tag the experiences this folder can be planned with</span>
          </Label>
          {expsForCat.length === 0 ? (
            <p className="text-[11px] text-stone-400 italic">No master experiences for {cat.label} yet - add one in Master Experiences.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {expsForCat.map(e => {
                const id = e.experience_id || e.id;
                const active = linkedSet.has(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggleExp(id)}
                    className={`text-[11px] rounded-full px-2.5 py-1 border transition ${active ? "bg-blue-700 text-white border-blue-700" : "bg-white border-stone-200 text-stone-700 hover:bg-blue-700 hover:text-white hover:border-blue-700"}`}
                    data-testid={`inspo-exp-${cat.id}-${id}`}
                  >
                    {e.name || id}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-[10px] text-stone-400 mt-1.5">Same tag system feeds every preference card so they show up when planning a matching experience.</p>
        </div>
      </CardContent>
    </Card>
  );
}
